import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { jobsApi, sftpApi } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import {
  History, ChevronRight, ChevronDown,
  Download, FileText, Loader, Database, Info, Building2} from 'lucide-react';
import toast from 'react-hot-toast';

function NoTenantSelected() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
      <Building2 size={40} style={{ color: 'var(--text-dim)', marginBottom: 16 }} />
      <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>No Tenant Selected</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)', maxWidth: 320, lineHeight: 1.6 }}>
        Use the tenant switcher in the sidebar to select a tenant and view their data.
      </p>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    success: 'badge-success', failed: 'badge-failed',
    partial: 'badge-partial', running: 'badge-running', pending: 'badge-pending',
  };
  return (
    <span className={`badge ${map[status] || 'badge-pending'}`}>
      <span className="badge-dot" />
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function JobRow({ job, onNavigate }) {
  const [expanded, setExpanded]     = useState(false);
  const [files, setFiles]           = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [downloading, setDownloading]   = useState({});

  const start    = new Date(job.started_at);
  const end      = job.finished_at ? new Date(job.finished_at) : null;
  const duration = end ? `${Math.round((end - start) / 1000)}s` : '—';

  const loadFiles = useCallback(async () => {
    if (files !== null) return; // already loaded
    setLoadingFiles(true);
    try {
      const { data } = await sftpApi.listFiles(100);
      // Filter to files produced by this job
      const jobFiles = data.filter(f => f.job_id === job.id);
      setFiles(jobFiles);
    } catch {
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [job.id, files]);

  const handleExpand = (e) => {
    e.stopPropagation();
    if (!expanded) loadFiles();
    setExpanded(o => !o);
  };

  const handleDownload = async (e, file) => {
    e.stopPropagation();
    setDownloading(d => ({ ...d, [file.id]: true }));
    try {
      let url = file.download_url;
      if (!url) {
        const res = await sftpApi.getDownloadUrl(file.id);
        url = res.data.download_url;
      }
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to get download link');
    } finally {
      setDownloading(d => ({ ...d, [file.id]: false }));
    }
  };

  const hasFiles = job.files_translated > 0;

  return (
    <>
      {/* Main job row */}
      <tr
        onClick={() => onNavigate(`/jobs/${job.id}`)}
        style={{ cursor: 'pointer' }}
      >
        <td>
          <div style={{ fontWeight: 500 }}>{format(start, 'MMM d, h:mm a')}</div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {formatDistanceToNow(start, { addSuffix: true })}
          </div>
        </td>
        <td><Badge status={job.status} /></td>
        <td style={{ color: 'var(--text-mid)', fontSize: 13 }}>{job.triggered_by}</td>
        <td>{job.files_translated}/{job.files_found > 0 ? job.files_found : '—'}</td>
        <td>{job.rows_processed > 0 ? job.rows_processed.toLocaleString() : '—'}</td>
        <td className="td-mono">{duration}</td>
        <td style={{ textAlign: 'right' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
            {hasFiles && (
              <button
                onClick={handleExpand}
                title={expanded ? 'Hide files' : 'Show translated files'}
                style={{
                  background: expanded ? 'rgba(37,99,235,0.12)' : 'none',
                  border: '1px solid',
                  borderColor: expanded ? 'rgba(37,99,235,0.3)' : 'var(--navy-border)',
                  borderRadius: 6, cursor: 'pointer', padding: '3px 8px',
                  display: 'flex', alignItems: 'center', gap: 4,
                  color: expanded ? 'var(--blue)' : 'var(--text-dim)',
                  fontSize: 11, fontWeight: 500,
                }}
              >
                <FileText size={11} />
                {job.files_translated} file{job.files_translated !== 1 ? 's' : ''}
                {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              </button>
            )}
            <ChevronRight size={16} style={{ color: 'var(--text-dim)' }} />
          </div>
        </td>
      </tr>

      {/* Expanded translated files row */}
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: 0, background: 'rgba(37,99,235,0.04)' }}>
            <div style={{
              borderTop: '1px solid rgba(37,99,235,0.15)',
              borderBottom: '1px solid rgba(37,99,235,0.15)',
              padding: '12px 20px 12px 48px',
            }}>
              {loadingFiles ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-dim)', fontSize: 13 }}>
                  <Loader size={13} className="spin" /> Loading files…
                </div>
              ) : files?.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-dim)', fontSize: 13 }}>
                  <Info size={13} />
                  No translated files linked to this job (files may have been produced before download tracking was enabled)
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {files?.map(file => (
                    <div
                      key={file.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px',
                        background: 'var(--navy)',
                        border: '1px solid var(--navy-border)',
                        borderRadius: 8,
                        minWidth: 200,
                      }}
                    >
                      <FileText size={14} style={{ color: 'var(--blue)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                          {file.file_name}
                        </div>
                        <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Database size={10} /> {file.rows?.toLocaleString() ?? '—'} rows
                          </span>
                          <span>{formatBytes(file.size_bytes)}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDownload(e, file)}
                        disabled={downloading[file.id]}
                        title="Download"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--blue)', padding: 4, flexShrink: 0,
                          opacity: downloading[file.id] ? 0.5 : 1,
                        }}
                      >
                        {downloading[file.id]
                          ? <Loader size={14} className="spin" />
                          : <Download size={14} />
                        }
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


export default function JobHistory() {
  const { user, activeTenantId } = useAuth();
  if (user?.is_admin && !activeTenantId) return <NoTenantSelected />;

  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate            = useNavigate();

  useEffect(() => {
    jobsApi.list(100)
      .then(({ data }) => { setJobs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">Job History</h1>
        <p className="page-subtitle">
          {jobs.length} sync jobs — click a row to view logs, or expand
          <FileText size={13} style={{ display: 'inline', margin: '0 3px', verticalAlign: 'middle' }} />
          to download translated files
        </p>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {jobs.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <History size={40} style={{ color: 'var(--text-dim)', margin: '0 auto 12px' }} />
            <h3 style={{ color: 'var(--text)' }}>No jobs yet</h3>
            <p style={{ color: 'var(--text-dim)' }}>Run your first sync from the Dashboard</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Triggered By</th>
                  <th>Files</th>
                  <th>Rows</th>
                  <th>Duration</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <JobRow key={job.id} job={job} onNavigate={navigate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Retention note */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', marginTop: 12,
        background: 'rgba(37,99,235,0.06)',
        borderRadius: 8, border: '1px solid rgba(37,99,235,0.15)',
        fontSize: 12, color: 'var(--text-dim)',
      }}>
        <Info size={13} style={{ color: 'var(--blue)', flexShrink: 0 }} />
        Translated files are retained for <strong style={{ color: 'var(--text-mid)' }}>90 days</strong> from
        the date of translation. Download links expire after 1 hour — click the download button to refresh.
      </div>
    </div>
  );
}
